/* eslint-disable jsx-a11y/label-has-associated-control */
import { useQueryClient } from '@tanstack/react-query';
import { CircleSolidInfoIcon, PortfolioBalanceIcon } from 'assets/icons';
import Dropdown, { DropdownItem } from 'components/Dropdown';
import useApiMutation from 'hooks/useApiMutation';
import moment from 'moment';
import { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppEventTypes } from 'types/AppType';
import { CheckoutOtp } from 'views/Accounts/components/Checkout.Com/CheckoutOtp';
import { usePortfolioBalances } from 'views/Portfolio/components/Summary/hooks/usePortfolioBalances';
import { HowToPayEnum } from 'views/shared/HowToPay/type';
import MgmtPaymentTypeSelector, { RadioBtnOption } from 'views/shared/MgmtFeePolicyEnforcer/mgmtPaymentTypeSelector';
import { useNavigate } from 'react-router-dom';
import { NavigationPath, PortfolioBalance } from 'types/DomainTypes';
import { AccountViewType, SubjectOptionKeys } from 'views/Accounts/types';
import { IPurchaseParams } from 'views/shared/services/executeBuy';
import { StatusTypes, useHandleBuy } from 'views/shared/hooks/useHandleBuy';
import RequestWineOnFailedPurchase from 'views/shared/SlideOutViews/RequestWineOnFailedPurchase';
import { PORTAL_DESCRIPTIONS } from 'types/commonTypes';
import { Button } from '../../../components';
import Accordion from '../../../components/Accordion';
import { FormSkeleton } from '../../../components/Skeleton';
import { ProductImage } from '../../../components/ProductTemplates';
import InvestMoreTemplate from '../../../components/ProductTemplates/components/InvestMoreTemplate';
import { MessageProductTemplateProp } from '../../../components/ProductTemplates/components/MessageTemplate';
import {
  BuySellHoldingModel,
  InvestMoreTemplateViewEnum,
  PricingType,
  ProductEventType,
  ViewStateType,
} from '../../../components/ProductTemplates/types';

import { AppContext } from '../../../context/ContextProvider';
import { getRegionColors, handleImageError } from '../../../helpers';
import { buildDisplayText, capitalizeFirstLetter, classNames, formatterGbp } from '../../../utils';
import useExecutor from '../../hooks/useExecutor';
import { FeedbackTemplate } from '../../shared/FeedbackTemplate';
import { getPaymentOptions, isAllowedCampaignPurchase, storeCampaignPurchases } from '../helpers';
import { useInvestDetails } from '../hooks/useInvestDetails';
import { investNowApi } from '../services/investNowApi';

import { InventTopUpEnum, InvestOffer, TabTypes, campaignPurchaseRefType } from '../types';

enum DisplayTextKeys {
  INVEST_NOW_TEXT = 'invest_now_text',
  NOTIFY_WHEN_AVAILABLE = 'notify_when_available_text',
  TITLE = 'title',
  PRICE = 'price',
}

interface InvestNowRequest {
  dealRef?: string;
  qty: number;
  purchasePrice?: number;
  requestPrice?: number;
}

type MessageType = Pick<MessageProductTemplateProp, 'title' | 'subTitle' | 'onClick' | 'buttonText'>;
interface InvestSlideoutProps {
  offer: InvestOffer;
  timestamp?: number;
  onClose?: () => void;
  requestTab: TabTypes;
  viewIn?: ViewStateType;
}

const InvestSlideout: FC<InvestSlideoutProps> = ({ onClose, requestTab, offer, viewIn = ViewStateType.DEFAULT }) => {
  const {
    state: {
      settings: { accountInfo },
      app: { hasCard },
    },
    dispatch,
    formatter,
  } = useContext(AppContext);

  const nav = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'invest:slideout', t), [t]);
  const [viewState, setViewState] = useState(ViewStateType.DEFAULT);
  const [feedbackState, setFeedbackState] = useState(ViewStateType.DEFAULT);
  const [purchaseInfo, setPurchaseInfo] = useState<campaignPurchaseRefType>();
  const [selectedOption, setSelectedOption] = useState<RadioBtnOption | null>();
  const [selectedPortfolio, setSelectedPortfolio] = useState<DropdownItem>();
  const isPrismicOffer = offer.offerSource === 'prismic';
  const [buySellModel, setBuySellModel] = useState<BuySellHoldingModel>({
    price: 0,
    units: 0,
    reason: '',
    pricingType: PricingType.MARKET,
    investMoreTemplateView: !isPrismicOffer ? InvestMoreTemplateViewEnum.CAMPAIGNS : null,
  });
  const [topupAmount, setTopAmount] = useState(0);

  const { portfolioBalances } = usePortfolioBalances();

  const feeModelInfo = useMemo(() => {
    const pBalances = portfolioBalances.filter((z) => `${z.portfolioId}`.length > 0);

    const feeModels = pBalances.filter((x: PortfolioBalance) => `${x.portfolioId}`.length > 0);
    const otherFeeModelExist = !!feeModels.find((x) => x.currentFeeModel === false);

    const canExecTransaction = (accountInfo?.account.totalCashOnAccount ?? 0) >= offer.price * buySellModel.units;

    const validTopupPortfolios = feeModels.filter(
      (x: PortfolioBalance) => `${x.portfolioId}`.length > 0 && !!x.currentFeeModel && x.currentFeeModel === true,
    );
    const canTopUp = validTopupPortfolios && validTopupPortfolios.length > 0;
    const ddlOptions = (
      canExecTransaction
        ? pBalances ?? []
        : pBalances.filter(
            (x: PortfolioBalance) => `${x.portfolioId}`.length > 0 && !!x.currentFeeModel && x.currentFeeModel === true,
          ) ?? []
    ).map((balance: PortfolioBalance) => {
      const { portfolioId, portfolioName } = balance;
      return {
        id: portfolioId,
        value: portfolioId,
        content: (
          <div className="flex justify-between text-base">
            <span>{portfolioName}</span>
          </div>
        ),
        text: portfolioName,
      };
    });

    if (ddlOptions.length === 1) setSelectedPortfolio(ddlOptions[0]);

    return {
      showFeeModelInfo: otherFeeModelExist,
      canTopUp,
      canExecTransaction,
      ddlOptions,
    };
  }, [portfolioBalances, offer, buySellModel, accountInfo]);

  const { sections, loading } = useInvestDetails({
    id: offer.id,
    enabled: isPrismicOffer,
  });
  const [feedbackTemplateConfig, setFeedbackTemplateConfig] = useState<MessageType>({
    onClick: () => null,
  });

  const purchaseInfoRef = useMemo(() => {
    return encodeURIComponent(btoa(JSON.stringify(purchaseInfo)));
  }, [purchaseInfo]);

  const { handlePurchase: executeBuy, mutateStatus: status, resetStatus } = useHandleBuy();

  const isAllowedPurchase = useMemo(() => {
    const numberOfUnitsAllowed = isAllowedCampaignPurchase({
      campaignId: offer?.campaignId,
      lwin18: offer.lwin18,
      quantity: offer.quantityOutstanding,
      numberOfUnitsAllowed: offer.numberOfUnitsAllowed,
      id: accountInfo?.portfolios?.map((x) => x.portfolioName).toString(),
    });
    return numberOfUnitsAllowed;
  }, [offer, accountInfo?.portfolios]);

  const options = useMemo((): RadioBtnOption[] => {
    return getPaymentOptions(hasCard as boolean);
  }, [hasCard]);

  const onExecutorStatusChange = (vState: ViewStateType, feedbackConfig?: MessageType) => {
    if (feedbackConfig) setFeedbackTemplateConfig(feedbackConfig);
    setFeedbackState(vState);
    setViewState(vState);
  };

  const executor = useExecutor({
    mutation: useApiMutation(investNowApi),
    viewRef: ViewStateType.INVEST_MORE,
    onStatusChange: onExecutorStatusChange,
    onClose: onClose || (() => null),
  });

  const accordionItems = useMemo(() => {
    return sections?.map((section, i) => ({
      id: `${i}`,
      title: `${section.title}`,
      titleContainerClassName: 'text-base p-5',
      content: () => <div className="text-14 p-3" dangerouslySetInnerHTML={{ __html: section.content }} />,
    }));
  }, [sections]);

  const onRequest = () => {
    if (requestTab === TabTypes.CURRENT) {
      setViewState(ViewStateType.INVEST_MORE);
    } else
      processInvestMore(ProductEventType.EXECUTE_BUY_PRODUCT_REQUEST, {
        qty: 0,
      });
  };

  const processBuyRequest = async (offerIn: InvestOffer) => {
    if (!selectedPortfolio) {
      setViewState(ViewStateType.SELECT_PORTFOLIO);
      return;
    }
    const {
      id: offerId,
      campaignId,
      lwin18,
      price: offerPrice,
      name: offerLabel,
      stringRef,
      priceGbp,
      exchangeRate: fxRate,
    } = offerIn;

    const purchaseRefData: campaignPurchaseRefType = {
      prevAcctBalance: accountInfo?.account.totalCashOnAccount,
      id: offerId,
      campaignId,
      portfolioId: Number(selectedPortfolio.value),
      lwin18,
      offerId: offerId,
      price: offerPrice,
      quantity: buySellModel.units,
      name: offerLabel,
      priceGbp,
      subtitle: '',
      stringRef: stringRef,
      userId: accountInfo?.portfolios?.map((x) => x.portfolioName).toString(),
    };

    setPurchaseInfo(purchaseRefData);

    const purchasePrice = price * buySellModel.units;
    const _topupAmount = purchasePrice - (accountInfo?.account.totalCashOnAccount ?? 0);

    if (_topupAmount > 0) {
      if (!feeModelInfo.canTopUp) {
        setViewState(ViewStateType.SELECT_PORTFOLIO);
        return;
      }
      setTopAmount(_topupAmount / (fxRate ?? 1));
      setViewState(ViewStateType.TOP_UP);
      return;
    }
    const params: IPurchaseParams = {
      body: {
        portfolioId: Number(selectedPortfolio.value),
        description: `${PORTAL_DESCRIPTIONS.PORTAL_INVEST}_${offer.stringRef}`,
        buys: [
          {
            lwin18: lwin18,
            offerId: offerId,
            price: priceGbp,
            quantity: buySellModel.units,
          },
        ],
      },
    };
    await executeBuy(params);
  };

  const processInvestMore = <T,>(eventType: ProductEventType, eventData: T) => {
    const { qty } = eventData as InvestNowRequest;
    const { name: _title, subtitle: _subTitle, priceGbp, expiryDate: _expiryDate } = offer;

    const investRequest = {
      offerTitle: _title,
      offerSubTitle: _subTitle,
      offerPrice: priceGbp,
      numberOfUnits: qty,
      totalPrice: Number(priceGbp) * qty,
      offerExpiryDate: _expiryDate,
    };

    executor(investRequest);
  };

  useEffect(() => {
    if (viewIn !== viewState) {
      setViewState(viewIn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIn]);

  useEffect(() => {
    if (status === StatusTypes.isLoading) {
      setViewState(ViewStateType.LOADING);
      setFeedbackState(ViewStateType.LOADING);
    }

    if (status === StatusTypes.error) setFeedbackState(ViewStateType.ERROR_RESULT);
    if (status === StatusTypes.success && feedbackState !== ViewStateType.CAMPAIGN_PURCHASE) {
      dispatch({
        type: AppEventTypes.UPDATE_STATE,
        payload: {
          campaignPurchaseRef: {
            id: offer.id,
            quantity: buySellModel.units,
          },
        },
      });
      storeCampaignPurchases({
        campaignId: offer.campaignId,
        lwin18: offer.lwin18,
        quantity: buySellModel.units,
        id: accountInfo?.portfolios?.map((x) => x.portfolioName).toString(),
      });
      setFeedbackState(ViewStateType.CAMPAIGN_PURCHASE);
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['fetchCampaignOffers'] });
        accountInfo?.account?.refetchCashBalances?.();
      }, 2000);
    }
  }, [
    status,
    buySellModel,
    offer,
    dispatch,
    accountInfo?.account,
    accountInfo?.portfolios,
    queryClient,
    feedbackState,
  ]);

  const {
    name: title,
    subtitle: subTitle,
    price,
    expiryDate,
    region,
    unitSize,
    mainImage,
    initialQuantity,
    quantityOutstanding,
    offerSource,
    exchangeRate: fxRate,
  } = offer;
  const { color: regionColor, textColor } = getRegionColors(region);
  const buttonText =
    displayText[
      requestTab === TabTypes.CURRENT ? DisplayTextKeys.INVEST_NOW_TEXT : DisplayTextKeys.NOTIFY_WHEN_AVAILABLE
    ];

  const calulateRemaingBal = (PortfolioView = false) => {
    const purchasePrice = price * buySellModel.units;
    const remaining = (accountInfo?.account.totalCashOnAccount ?? 0) - purchasePrice;
    let btnText = '';
    if (feeModelInfo.ddlOptions.length > 1 && !PortfolioView) {
      btnText = 'Select portfolio';
    } else if (remaining >= 0) {
      btnText = 'Confirm purchase';
    } else {
      btnText = 'Continue to top-up';
    }

    return { remainingBal: remaining, btnText };
  };

  return (
    <div className="flex flex-col flex-1 bg-gray-50 dark:bg-slate-900 pb-5 px-4 w-full relative overflow-x-hidden sm:w-[390px]">
      <div className="w-full h-full rounded-2xl overflow-y-auto flex flex-col">
        {status === StatusTypes.error && (
          <div className="flex flex-col bg-white dark:bg-slate-800 flex-1 h-full rounded-2xl shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700">
            <RequestWineOnFailedPurchase
              offer={offer}
              onStatusChange={onExecutorStatusChange}
              resetStatus={resetStatus}
              onClose={onClose}
              qtyBought={buySellModel.units}
            />
          </div>
        )}

        {viewState === ViewStateType.SELECT_PORTFOLIO && status !== StatusTypes.error && (
          <div className="flex flex-col bg-white dark:bg-slate-800 h-full p-5 rounded-2xl shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700">
            <div className="flex flex-1 flex-col">
              {feeModelInfo.showFeeModelInfo && (
                <div className="mb-8 p-4 bg-amber-50 dark:bg-amber-500/10 rounded-xl border border-amber-200 dark:border-amber-500/20">
                  <div className="flex items-center w-full mb-3">
                    <div className="mr-3 text-gray-900 dark:text-white font-medium">{t`account:slideout.topup.recurring_payment_info_title`}</div>
                    <CircleSolidInfoIcon className="text-amber-500" />
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 w-full flex-1 h-full overflow-x-hidden">
                    {t(`account:slideout.topup.current_fee_model_info`)}
                  </p>
                </div>
              )}
              {feeModelInfo.ddlOptions.length > 1 && (feeModelInfo.canExecTransaction || feeModelInfo.canTopUp) && (
                <>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('invest:selectPortfolio', 'Select Portfolio')}
                  </label>
                  <Dropdown
                    placeholder="Select portfolio"
                    value={selectedPortfolio?.text}
                    containerClassName="w-full rounded-xl mb-5"
                    itemsContainerClassName="max-h-[250px] min-h-10 overflow-y-auto bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg"
                    className="flex-1 text-sm text-gray-900 dark:text-white border border-gray-200 dark:border-slate-600 h-12 py-2 px-4 rounded-xl justify-between items-center bg-white dark:bg-slate-700/50 hover:border-orange dark:hover:border-teal-500 transition-colors"
                    itemsWrapperClassName="w-full"
                    items={feeModelInfo.ddlOptions}
                    onItemSelect={(item) => {
                      setSelectedPortfolio(item);
                    }}
                  />
                  <Button
                    isDisable={!selectedPortfolio || !feeModelInfo.canTopUp}
                    className="my-5 text-sm font-medium rounded-full btn flex justify-center items-center py-3 text-white bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 shadow-lg shadow-orange/25 dark:shadow-teal-500/25 disabled:opacity-50 disabled:shadow-none transition-all"
                    onClick={() => processBuyRequest(offer)}
                    props={{
                      name: 'requestTab',
                    }}
                  >
                    {calulateRemaingBal(true).btnText}
                  </Button>
                </>
              )}

              {!feeModelInfo.canTopUp && !feeModelInfo.canExecTransaction && (
                <Button
                  className="my-5 text-sm font-medium rounded-full btn flex justify-center items-center py-3 text-white bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 shadow-lg shadow-orange/25 dark:shadow-teal-500/25 transition-all"
                  onClick={() => {
                    nav(NavigationPath.ACCOUNTS, {
                      state: {
                        accountViewType: AccountViewType.CONTACT_US,
                        subject: SubjectOptionKeys.FINANCIAL,
                      },
                    });
                  }}
                  props={{
                    name: 'requestTab',
                  }}
                >
                  {t('common:contactUs', 'Contact us')}
                </Button>
              )}
            </div>
          </div>
        )}

        {viewState === ViewStateType.TOP_UP && (
          <div className="bg-white dark:bg-slate-800 h-full rounded-2xl shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700 overflow-hidden">
            <div className="flex items-center flex-col p-6 gap-3 bg-gradient-to-br from-orange/5 to-orange/10 dark:from-teal-500/10 dark:to-teal-500/5 border-b border-gray-100 dark:border-slate-700">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange/20 to-orange/10 dark:from-teal-500/20 dark:to-teal-500/10 flex items-center justify-center">
                <PortfolioBalanceIcon className="w-6 h-6 text-orange dark:text-teal-400" />
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {t('account:slideout.topup.top_up_amount')}
              </span>
              <span className="text-center text-2xl font-bold text-gray-900 dark:text-white">{`${formatter.format(
                topupAmount * (fxRate ?? 1),
                true,
              )}`}</span>
              {(fxRate ?? 1) !== 1 && (
                <>
                  <span className="text-center text-lg text-gray-500 dark:text-gray-400">{`${formatterGbp.format(
                    topupAmount,
                  )}`}</span>
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-500/10 rounded-xl border border-amber-200 dark:border-amber-500/20">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      <strong className="text-gray-900 dark:text-white mr-1">
                        {t('common:pleaseNote', 'Please note:')}
                      </strong>
                      {t(
                        'invest:fxNote',
                        'The amount displayed in your default currency is for reference only. All transactions will be processed and charged in GBP.',
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>
            <MgmtPaymentTypeSelector
              onSelected={(selected) => setSelectedOption(selected)}
              options={options}
              selectedId={selectedOption?.id}
            />

            <CheckoutOtp
              showTopUpIcon={false}
              isUseExistingCard={selectedOption?.id === (HowToPayEnum.CARD_ON_FILE as string)}
              amount={topupAmount}
              portfolioId={Number(selectedPortfolio?.value) || -1}
              onSuccessUrl={`#${InventTopUpEnum.onInvestSuccess}=${purchaseInfoRef}`}
              onFailureUrl={`#${InventTopUpEnum.onInvestFailure}=${purchaseInfoRef}`}
            />
          </div>
        )}

        {viewState !== ViewStateType.TOP_UP &&
          viewState !== ViewStateType.SELECT_PORTFOLIO &&
          status !== StatusTypes.error && (
            <FeedbackTemplate
              onClose={() => {
                if (status === StatusTypes.isLoading) return;
                if (onClose) onClose();
              }}
              templateConfig={feedbackTemplateConfig}
              viewState={feedbackState}
              onCTA={(vState) => {
                if (feedbackState === ViewStateType.CAMPAIGN_PURCHASE) {
                  nav(`${NavigationPath.MY_PORTFOLIO}#find=${purchaseInfoRef}`);
                  return;
                }
                setFeedbackState(vState);
              }}
            >
              <>
                <ProductImage
                  bgColor={regionColor || '#757587'}
                  region={region ? capitalizeFirstLetter(region) : ''}
                  textColor={textColor}
                >
                  <img
                    className="h-[220px] object-contain drop-shadow-lg mx-auto"
                    alt={title}
                    src={mainImage}
                    onError={handleImageError}
                  />
                </ProductImage>
                <div className="flex flex-col bg-white dark:bg-slate-800 flex-1 h-100">
                  <div className="px-4 pt-4 pb-2">
                    <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white leading-snug">
                      {title}
                    </h2>
                    {subTitle && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{subTitle}</p>}
                  </div>
                  {/* Stats Card */}
                  <div className="mx-4 my-3">
                    <div className="bg-gray-50 dark:bg-slate-800 rounded-xl p-3 border border-gray-100 dark:border-slate-700/50 shadow-sm">
                      {/* Price row */}
                      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-100 dark:border-slate-700">
                        <span className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
                          Price
                        </span>
                        <span className="text-base font-bold text-orange dark:text-teal-400">
                          {formatter.format(Number(price), true)}
                        </span>
                      </div>
                      {/* Unit size + expiry columns */}
                      <div className="flex justify-center divide-x divide-gray-200 dark:divide-slate-700">
                        <div className="flex flex-col items-center flex-1 px-3">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">
                            Unit Size
                          </span>
                          <span className="text-sm text-gray-900 dark:text-white font-semibold">{unitSize}</span>
                        </div>
                        <div className="flex flex-col items-center flex-1 px-3">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-0.5">
                            Expiry
                          </span>
                          <span className="text-sm text-gray-900 dark:text-white font-semibold">
                            {moment(expiryDate).format('YYYY-MM-DD')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Stock Badge */}
                  {!isPrismicOffer && (
                    <div className="mx-4 mb-3">
                      <div
                        className={classNames(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border',
                          quantityOutstanding <= 5
                            ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 font-medium'
                            : 'border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 text-gray-600 dark:text-gray-300',
                        )}
                      >
                        {quantityOutstanding <= 5 && (
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                          </span>
                        )}
                        {quantityOutstanding} / {initialQuantity} {t('invest:available', 'available')}
                      </div>
                    </div>
                  )}

                  {viewState === ViewStateType.INVEST_MORE && (
                    <InvestMoreTemplate
                      formatter={{
                        format: (val: number) => formatter.format(val, true),
                      }}
                      canCustomPrice={false}
                      onCTA={processInvestMore}
                      marketPrice={Number(price)}
                      model={buySellModel}
                      qtyOwned={0}
                      setModel={(modelIn) => setBuySellModel(modelIn)}
                      overideOnCTA={!isPrismicOffer ? [() => processBuyRequest(offer)] : null}
                      quantity={!isPrismicOffer ? isAllowedPurchase?.qty || 0 : null}
                      isPrismicOffer={isPrismicOffer}
                      extraData={{
                        buttonText: calulateRemaingBal().btnText,
                        accountBalance: accountInfo?.account.totalCashOnAccount ?? 0,
                        accountBalanceRemaining: calulateRemaingBal().remainingBal,
                        isLD: offerSource === 'LD',
                      }}
                    />
                  )}

                  {viewState === ViewStateType.DEFAULT && (
                    <>
                      <Button
                        isDisable={!isAllowedPurchase?.isAllowed}
                        className="mx-4 my-5 text-sm font-medium rounded-full btn flex justify-center items-center py-3 text-white bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl disabled:opacity-50 disabled:shadow-none transition-all"
                        onClick={onRequest}
                        props={{
                          name: requestTab,
                        }}
                      >
                        {buttonText}
                      </Button>

                      {!isAllowedPurchase?.isAllowed ? (
                        <span className="text-sm mx-4 text-red-500 dark:text-red-400 text-center">{`Only a max of ${offer.numberOfUnitsAllowed} units are allowed per customer`}</span>
                      ) : null}

                      {loading && isPrismicOffer ? (
                        <div className="h-full w-full flex-1 p-5">
                          <FormSkeleton fields={3} />
                        </div>
                      ) : (
                        <>
                          <Accordion
                            className="divide-y divide-gray-100 dark:divide-slate-700 mx-4 mt-4 rounded-xl overflow-hidden border border-gray-100 dark:border-slate-700"
                            items={accordionItems || []}
                          />
                          <div className="pb-[100px]" />
                        </>
                      )}
                    </>
                  )}
                </div>
              </>
            </FeedbackTemplate>
          )}
      </div>
    </div>
  );
};

export default InvestSlideout;
